import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import './CourierMonitor.css';
import { getCourierMonitorOrders } from '../../services/courierMonitorService';

const MAX_NUMBER_FONT_PX = 52;
const MIN_NUMBER_FONT_PX = 12;
const CONTROLS_HIDE_DELAY_MS = 3000;

function FitNumber({ text }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fit = () => {
      const box = el.parentElement;
      if (!box) return;
      let size = MAX_NUMBER_FONT_PX;
      el.style.fontSize = `${size}px`;
      while (
        size > MIN_NUMBER_FONT_PX &&
        (el.scrollWidth > box.clientWidth || el.scrollHeight > box.clientHeight)
      ) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }
    };

    fit();
    const ro = new ResizeObserver(fit);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => ro.disconnect();
  }, [text]);

  return (
    <span className="cm-card-number" ref={ref}>
      {text}
    </span>
  );
}

const ORIGIN_STYLES = {
  'Яндекс.Еда': { label: 'Яндекс', bg: '#FFCC00', color: '#1a1a1a' },
  UzumTezkor: { label: 'Uzum', bg: '#7C3AED', color: '#ffffff' },
  Wolt: { label: 'Wolt', bg: '#00C2E8', color: '#0d0d0d' },
  'Web-panel': { label: 'Сайт', bg: '#f97316', color: '#ffffff' },
};
const DEFAULT_ORIGIN_STYLE = { label: 'Доставка', bg: '#6b7280', color: '#ffffff' };

const POLL_INTERVAL_MS = 5000;

function formatOrderNumber(order) {
  const external = order.external_order_number;
  if (external) {
    const dashIndex = external.lastIndexOf('-');
    return dashIndex === -1 ? external : external.slice(dashIndex + 1);
  }
  return order.number;
}

function getInitialLayout() {
  const saved = localStorage.getItem('courierMonitorLayout');
  if (saved === 'horizontal' || saved === 'vertical') return saved;
  return window.matchMedia('(orientation: portrait)').matches ? 'vertical' : 'horizontal';
}

const IconFullscreenEnter = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
  </svg>
);

const IconFullscreenExit = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
  </svg>
);

const CourierMonitor = () => {
  const { organizationId } = useParams();
  const [preparing, setPreparing] = useState([]);
  const [ready, setReady] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [layout, setLayout] = useState(getInitialLayout);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const mouseTimerRef = useRef(null);

  const fetchOrders = useCallback(async () => {
    if (!organizationId) return;
    try {
      const data = await getCourierMonitorOrders(organizationId);
      setPreparing(data.preparing || []);
      setReady(data.ready || []);
      setError(null);
    } catch (err) {
      console.error('Courier monitor fetch error:', err);
      setError('Нет связи с сервером');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchOrders();
    const id = setInterval(fetchOrders, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchOrders]);

  // Fullscreen state sync
  useEffect(() => {
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  // Auto-hide controls on mouse inactivity
  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (mouseTimerRef.current) clearTimeout(mouseTimerRef.current);
    mouseTimerRef.current = setTimeout(() => setShowControls(false), CONTROLS_HIDE_DELAY_MS);
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', resetHideTimer);
    mouseTimerRef.current = setTimeout(() => setShowControls(false), CONTROLS_HIDE_DELAY_MS);
    return () => {
      document.removeEventListener('mousemove', resetHideTimer);
      if (mouseTimerRef.current) clearTimeout(mouseTimerRef.current);
    };
  }, [resetHideTimer]);

  const toggleLayout = useCallback(() => {
    setLayout((prev) => {
      const next = prev === 'horizontal' ? 'vertical' : 'horizontal';
      localStorage.setItem('courierMonitorLayout', next);
      return next;
    });
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.warn('Fullscreen error:', err);
      });
    } else {
      document.exitFullscreen();
    }
  }, []);

  const renderCard = (order) => {
    const originStyle = ORIGIN_STYLES[order.origin_name] || DEFAULT_ORIGIN_STYLE;
    return (
      <div className="cm-card" key={order.id}>
        <span
          className="cm-card-badge"
          style={{ background: originStyle.bg, color: originStyle.color }}
        >
          {originStyle.label}
        </span>
        <div className="cm-card-number-box">
          <FitNumber text={String(formatOrderNumber(order))} />
        </div>
      </div>
    );
  };

  if (!organizationId) {
    return (
      <div className="cm-container">
        <h1>Неверный адрес монитора</h1>
      </div>
    );
  }

  return (
    <div className={`cm-container cm-layout-${layout}${showControls ? '' : ' cm-hide-cursor'}`}>
      <div className={`cm-controls${showControls ? '' : ' cm-controls-hidden'}`}>
        <button className="cm-btn" onClick={toggleLayout} title="Сменить ориентацию">
          {layout === 'horizontal' ? '⬍' : '⬌'}
        </button>
        <button className="cm-btn" onClick={toggleFullscreen} title={isFullscreen ? 'Выйти из полноэкранного режима' : 'Полноэкранный режим'}>
          {isFullscreen ? <IconFullscreenExit /> : <IconFullscreenEnter />}
        </button>
      </div>

      {error && <div className="cm-error-banner">⚠️ {error}</div>}

      <div className="cm-columns">
        <section className="cm-column cm-column-preparing">
          <header className="cm-column-header cm-header-preparing">
            <span className="cm-column-icon">🔥</span>
            <span>ГОТОВИТСЯ</span>
            <span className="cm-column-count">{preparing.length}</span>
          </header>
          <div className="cm-cards">
            {loading ? (
              <div className="cm-empty">Загрузка...</div>
            ) : preparing.length === 0 ? (
              <div className="cm-empty">Нет заказов</div>
            ) : (
              preparing.map(renderCard)
            )}
          </div>
        </section>

        <section className="cm-column cm-column-ready">
          <header className="cm-column-header cm-header-ready">
            <span className="cm-column-icon">✅</span>
            <span>ГОТОВО</span>
            <span className="cm-column-count">{ready.length}</span>
          </header>
          <div className="cm-cards">
            {loading ? (
              <div className="cm-empty">Загрузка...</div>
            ) : ready.length === 0 ? (
              <div className="cm-empty">Нет заказов</div>
            ) : (
              ready.map(renderCard)
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CourierMonitor;
